import { useState } from 'react'
import { FilterChip } from './chip'
import { Popover } from './popover'

export type DateRange = { from: string; to: string }

type PresetId =
  | 'today'
  | 'last7'
  | 'last30'
  | 'thisMonth'
  | 'lastMonth'
  | 'thisQuarter'
  | 'lastQuarter'
  | 'thisYear'
  | 'lastYear'
  | 'all'

const PRESETS: { id: PresetId; label: string }[] = [
  { id: 'today', label: 'Сьогодні' },
  { id: 'last7', label: 'Останні 7 днів' },
  { id: 'last30', label: 'Останні 30 днів' },
  { id: 'thisMonth', label: 'Цей місяць' },
  { id: 'lastMonth', label: 'Минулий місяць' },
  { id: 'thisQuarter', label: 'Цей квартал' },
  { id: 'lastQuarter', label: 'Минулий квартал' },
  { id: 'thisYear', label: 'Цей рік' },
  { id: 'lastYear', label: 'Минулий рік' },
  { id: 'all', label: 'Весь період' },
]

/** Local yyyy-mm-dd (avoids the UTC off-by-one of toISOString). */
function ymd(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

/** Concrete from/to for a preset. "Current" presets end at today; past ones span the full period. */
export function presetRange(id: PresetId, today: Date = new Date()): DateRange {
  const y = today.getFullYear()
  const mo = today.getMonth()
  switch (id) {
    case 'today':
      return { from: ymd(today), to: ymd(today) }
    case 'last7': {
      const f = new Date(today)
      f.setDate(f.getDate() - 6)
      return { from: ymd(f), to: ymd(today) }
    }
    case 'last30': {
      const f = new Date(today)
      f.setDate(f.getDate() - 29)
      return { from: ymd(f), to: ymd(today) }
    }
    case 'thisMonth':
      return { from: ymd(new Date(y, mo, 1)), to: ymd(today) }
    case 'lastMonth':
      return { from: ymd(new Date(y, mo - 1, 1)), to: ymd(new Date(y, mo, 0)) }
    case 'thisQuarter': {
      const q = Math.floor(mo / 3)
      return { from: ymd(new Date(y, q * 3, 1)), to: ymd(today) }
    }
    case 'lastQuarter': {
      const q = Math.floor(mo / 3)
      return { from: ymd(new Date(y, q * 3 - 3, 1)), to: ymd(new Date(y, q * 3, 0)) }
    }
    case 'thisYear':
      return { from: ymd(new Date(y, 0, 1)), to: ymd(today) }
    case 'lastYear':
      return { from: ymd(new Date(y - 1, 0, 1)), to: ymd(new Date(y - 1, 11, 31)) }
    case 'all':
      return { from: '2000-01-01', to: ymd(today) }
  }
}

/** Which preset (if any) the current from/to exactly equals — drives the chip label. */
function matchPreset(from: string, to: string): PresetId | null {
  const today = new Date()
  for (const { id } of PRESETS) {
    const r = presetRange(id, today)
    if (r.from === from && r.to === to) return id
  }
  return null
}

const dateInputStyle: React.CSSProperties = {
  height: 26,
  flex: 1,
  minWidth: 0,
  background: 'var(--bg-1)',
  border: '1px solid var(--border-default)',
  color: 'var(--fg-1)',
  borderRadius: 6,
  padding: '0 6px',
  fontSize: 11,
  fontFamily: 'inherit',
}

type Props = {
  from: string
  to: string
  onChange: (from: string, to: string) => void
  label?: string
  /** When true, an empty range is allowed and the chip exposes a clear (x). */
  clearable?: boolean
}

/** Filter chip with a preset menu (today / this month / last quarter / …) plus a custom range. */
export function DateRangeChip({ from, to, onChange, label = 'Діапазон', clearable }: Props) {
  const [open, setOpen] = useState(false)
  const hasRange = from !== '' && to !== ''
  const matched = hasRange ? matchPreset(from, to) : null
  const value = !hasRange
    ? undefined
    : matched
      ? PRESETS.find((p) => p.id === matched)!.label
      : `${from} — ${to}`

  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <FilterChip
        label={label}
        value={value}
        active={open}
        onClick={() => setOpen(!open)}
        onClear={clearable && hasRange ? () => onChange('', '') : undefined}
      />
      <Popover open={open} onClose={() => setOpen(false)} width={300}>
        <div style={{ padding: 4 }}>
          {PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => {
                const r = presetRange(p.id)
                onChange(r.from, r.to)
                setOpen(false)
              }}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '7px 10px',
                fontSize: 12,
                borderRadius: 6,
                background: matched === p.id ? 'var(--bg-3)' : 'transparent',
                border: 0,
                color: 'var(--fg-1)',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div style={{ borderTop: '1px solid var(--border-subtle)', padding: '10px 12px 12px' }}>
          <div style={{ fontSize: 11, color: 'var(--fg-3)', marginBottom: 6 }}>Свій діапазон</div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input
              type="date"
              value={from}
              max={to || undefined}
              onChange={(e) => onChange(e.target.value, to)}
              style={dateInputStyle}
            />
            <span style={{ color: 'var(--fg-4)' }}>—</span>
            <input
              type="date"
              value={to}
              min={from || undefined}
              onChange={(e) => onChange(from, e.target.value)}
              style={dateInputStyle}
            />
          </div>
        </div>
      </Popover>
    </span>
  )
}
